import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { SALES_STAGES } from "@/lib/sales";
import LeadCard from "@/components/sales/LeadCard";
import LeadDetailDrawer from "@/pages/sales/LeadDetailDrawer";
import MeetingModal from "@/components/sales/MeetingModal";
import WonGateModal from "@/components/sales/WonGateModal";
import LossModal from "@/components/sales/LossModal";
import ScopeToggle, { readScope } from "@/components/common/ScopeToggle";
import { useAuth } from "@/context/AuthContext";
import { isSalesManagerRole } from "@/lib/roleAliases";

export default function PipelinePage() {
  const { roleName } = useAuth();
  const isSalesManager = isSalesManagerRole(roleName);
  const [pipeline, setPipeline] = useState({});
  const [executives, setExecutives] = useState([]);
  const [selected, setSelected] = useState(null);
  const [gate, setGate] = useState({ type: null, lead: null });
  const [scope, setScope] = useState(readScope("sm.pipeline.scope", "mine"));

  const load = async () => {
    try {
      const [p, e] = await Promise.all([
        api.get(`/sales/pipeline${isSalesManager ? `?scope=${scope}` : ""}`),
        api.get("/sales/executives"),
      ]);
      setPipeline(p.data);
      setExecutives(e.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [scope]);

  const onDropTo = async (stageId, ev) => {
    ev.preventDefault();
    const leadId = ev.dataTransfer.getData("lead");
    if (!leadId) return;
    const allLeads = Object.values(pipeline).flat();
    const lead = allLeads.find(x => x.id === leadId);
    if (!lead || lead.status === stageId) return;

    if (stageId === "meeting_scheduled") { setGate({ type: "meeting", lead }); return; }
    if (stageId === "won") { setGate({ type: "won", lead }); return; }
    if (stageId === "loss") { setGate({ type: "loss", lead }); return; }

    try {
      await api.patch(`/sales/leads/${leadId}/status`, { status: stageId });
      toast.success(`Moved to ${SALES_STAGES.find(s => s.id === stageId)?.label}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Sales · Pipeline</p>
            <h1 className="font-display text-3xl text-white mt-1">Pipeline Board</h1>
            <p className="text-xs text-gray-500 font-mono mt-1">Drag lead cards across columns to advance. Certain transitions require a data gate.</p>
          </div>
          {isSalesManager && (
            <ScopeToggle
              value={scope}
              onChange={setScope}
              storageKey="sm.pipeline.scope"
              testIdPrefix="sm-pipeline-scope"
            />
          )}
        </div>
      </div>

      <div className="px-10 py-8 overflow-x-auto">
        <div className="flex gap-4 min-w-max pb-8">
          {SALES_STAGES.map((s) => {
            const cards = pipeline[s.id] || [];
            return (
              <div
                key={s.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDropTo(s.id, e)}
                className={`w-[260px] iz-card p-3 flex flex-col`}
                data-testid={`kanban-col-${s.id}`}
              >
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#1a1a1a]">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: s.color }}/>
                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-white">{s.label}</p>
                  </div>
                  <span className="text-[10px] font-mono text-gray-500">{cards.length}</span>
                </div>
                <div className="space-y-2 min-h-[100px]">
                  {cards.length === 0 && (
                    <p className="text-[10px] text-gray-600 text-center py-6 border border-dashed border-[#262626] rounded">Drop here</p>
                  )}
                  {cards.map(c => (
                    <LeadCard
                      key={c.id}
                      lead={c}
                      onClick={() => setSelected(c)}
                      draggable
                      onDragStart={(ev) => ev.dataTransfer.setData("lead", c.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <LeadDetailDrawer lead={selected} onClose={() => setSelected(null)} onChanged={load} executives={executives}/>
      <MeetingModal open={gate.type === "meeting"} lead={gate.lead} onClose={() => setGate({type:null,lead:null})} onSaved={() => { setGate({type:null,lead:null}); load(); }}/>
      <WonGateModal open={gate.type === "won"} lead={gate.lead} onClose={() => setGate({type:null,lead:null})} onSaved={() => { setGate({type:null,lead:null}); load(); }}/>
      <LossModal open={gate.type === "loss"} lead={gate.lead} onClose={() => setGate({type:null,lead:null})} onSaved={() => { setGate({type:null,lead:null}); load(); }}/>
    </div>
  );
}
